import clsx from 'clsx';
import type { HTMLProps, MutableRefObject, ReactNode } from 'react';
import React, { useRef, useState } from 'react';
import FocusLock from 'react-focus-lock';
import {
  useOutsideClick,
  useOnWindowResize,
  useBoundingclientrect,
} from 'rooks';
import { createContext, useContext } from 'use-context-selector';
import styles from './DropdownMenu.module.scss';

type DropdownContext = {
  isExpanded: boolean;
  toggle(): void;
  open(): void;
  close(): void;
  buttonRef: MutableRefObject<HTMLButtonElement | null>;
  listRef: MutableRefObject<HTMLDivElement | null>;
};

const DropdownContext = createContext<DropdownContext | undefined>(undefined);

/**
 * Dropdown Menu component with dropdown context.
 * @param props
 */
export const DropdownMenu = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const [isExpanded, setExpanded] = useState(false);

  const open = () => {
    setExpanded(true);
  };

  const close = () => {
    setExpanded(false);
  };

  const toggle = () => {
    setExpanded(!isExpanded);
  };

  const handleOutsideClick = () => {
    close();
  };

  const handleResize = () => {
    if (isExpanded) {
      close();
    }
  };

  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const contextValue = {
    isExpanded,
    toggle,
    open,
    close,
    buttonRef,
    listRef,
  };

  /* Setup hooks  */
  useOutsideClick(menuRef, handleOutsideClick, isExpanded);
  useOnWindowResize(handleResize);

  return (
    <div
      ref={menuRef}
      className={clsx(styles.dropdownMenu, className)}
      data-wrapper="clickoutside"
    >
      <DropdownContext.Provider value={contextValue}>
        {children}
      </DropdownContext.Provider>
    </div>
  );
};

/**
 * Dropdown Button component
 * - Displays button and handles dropdown open/close toggling
 */
export const DropdownButton = (props: HTMLProps<HTMLButtonElement>) => {
  const dropdownContext = useContext(DropdownContext);

  if (dropdownContext === undefined)
    throw new Error('DropdownButton must be used within a DropdownContext');

  const { toggle: toggleDropdown, isExpanded, buttonRef } = dropdownContext;
  const { children, className, ...rest } = props;

  return (
    <button
      ref={buttonRef}
      className={clsx(styles.dropdownMenu__button, className)}
      onClick={toggleDropdown}
      {...rest}
      aria-expanded={isExpanded}
      type="button"
    >
      {children}
    </button>
  );
};

type dropdownListDirectionX = 'left' | 'right';

/**
 * Dropdown list component
 * - Outputs a div to hold the dropdown content.
 */
export const DropdownList = ({
  directionX = 'left',
  ...props
}: HTMLProps<HTMLDivElement> & {
  directionX?: dropdownListDirectionX;
}) => {
  const dropdownContext = useContext(DropdownContext);

  if (dropdownContext === undefined)
    throw new Error('DropdownButton must be used within a DropdownContext');

  const { isExpanded, listRef, buttonRef, close } = dropdownContext;
  const { children, className, ...rest } = props;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  };

  const listRect = useBoundingclientrect(listRef) as DOMRect;
  const buttonRect = useBoundingclientrect(buttonRef) as DOMRect;
  const position = positionDefault(buttonRect, listRect, directionX);

  return (
    <FocusLock disabled={!isExpanded} returnFocus persistentFocus={true}>
      {/* a11y override to handle keydown listener without needing to specify focusable children */}
      {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
      <div
        ref={listRef}
        className={clsx(styles.dropdownMenu__list, className)}
        hidden={!isExpanded}
        role="menu"
        style={{
          ...position,
        }}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {children}
      </div>
    </FocusLock>
  );
};

/**
 * Calculate position for dropdown element.
 * - Generate a left and top attribute based on collisions and element positions.
 * @param targetRect - DOMRect for the target element
 * @param popoverRect - DOMRect for the popover element
 * @param directionX - default direction for the popup
 * @returns
 */
const positionDefault = (
  targetRect: DOMRect,
  popoverRect: DOMRect,
  directionX: dropdownListDirectionX
) => {
  if (!targetRect || !popoverRect) {
    return;
  }

  const { directionRight, directionUp, directionLeft } = getCollisions(
    targetRect,
    popoverRect
  );

  const displayPopoverRight =
    (directionX === 'right' && !directionLeft) || directionRight;

  return {
    left: displayPopoverRight
      ? `${
          targetRect.right -
          popoverRect.width +
          window.scrollX -
          targetRect.left
        }px`
      : `${window.scrollX}px`,
    ...getTopPosition(targetRect, popoverRect, directionUp),
  };
};

/**
 * Calculate top position.
 * - Generates top position based on the `isDirectionUp` boolean.
 * - Positions popover either below or above the button element.
 * @param targetRect - DOMRect for the target element
 * @param popoverRect - DOMRect for the popover element
 * @param isDirectionUp - whether the popover should be above or below the target.
 */
const getTopPosition = (
  targetRect: DOMRect,
  popoverRect: DOMRect,
  isDirectionUp: boolean
) => {
  return {
    top: isDirectionUp
      ? `${popoverRect.height * -1}px`
      : `${targetRect.height}px`,
  };
};

/**
 * Calculate element collisions.
 * - Checks if target and popover are overlapping.
 * - Checks if popover is going to go offscreen.
 * - Returns booleans to indicate if any directions are offscreen.
 * @param targetRect - DOMRect for target
 * @param popoverRect - DOMRect for popover
 * @param offsetLeft - offsetLeft
 * @param offsetBottom - offsetRight
 * @returns - Object of booleans for top, left, bottom, right.
 */
const getCollisions = (
  targetRect: DOMRect,
  popoverRect: DOMRect,
  offsetLeft = 0,
  offsetBottom = 0
) => {
  const collisions = {
    top: targetRect.top - popoverRect.height < 0,
    right: window.innerWidth < targetRect.left + popoverRect.width - offsetLeft,
    bottom:
      window.innerHeight <
      targetRect.bottom + popoverRect.height - offsetBottom,
    left: targetRect.left + targetRect.width - popoverRect.width < 0,
  };

  const directionRight = collisions.right && !collisions.left;
  const directionLeft = collisions.left && !collisions.right;
  const directionUp = collisions.bottom && !collisions.top;
  const directionDown = collisions.top && !collisions.bottom;

  return { directionRight, directionLeft, directionUp, directionDown };
};
